import { css } from '@emotion/css';
import React, { Fragment, useEffect, useState } from 'react';
import { SupportedLanguages, PreviewLayout } from 'utility/constants';
import { setAppUser, setCurrentShape } from './command.currentSvg';
import { useTheme } from './componentLib/ShapesTheme';
import { getDefaultShapes, getShapes } from './shapeData/referenceData.shapes';
import { OptionValueType, Shape } from './shapeData/types.shapes';
import i18n from './i18n';

import ShapeTypeList from './ShapeTypeList';
import { resources } from './i18n.resources';
import { useBreakpointInfo } from '../../customHooks/useBreakpointInfo';
import { AuthConfig, FlipOptions } from './types';
import { DragAndDrop, enbaleDragAndDrop } from './dragAndDrop';
import { EditorView } from './EditorView';
import { getShapesAssetList } from '../../services/shapesAsset.service';

export interface SmartShapesProps {
    locale?: string;
    hostApp: string;
    tenantId: string;
    authConfig: AuthConfig;
    layout?: PreviewLayout;
    dragAndDrop?: DragAndDrop;
}

const getLayoutContainerStyle = () => css`
        width: 100%;
        height: 100%;
        display: flex;
        overflow: hidden;

        .smart-shapes__list-view {
            display: flex;
            box-sizing: border-box;
            flex-direction: column;
            width: 100%;
        }

        .smart-shapes__editor-view {
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
        }

        .shape-type-list {
            overflow-y: auto;
            height: 100%;
        }
    `;

const customScrollStyle = css`
    *::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    *::-webkit-scrollbar-track {
        background: #FFF;
    }

    *::-webkit-scrollbar-thumb {
        background: #C4CDD6;
        border-radius: 8px;
    }

    *::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    *::-moz-scrollbar {
        width: 8px;
        height: 8px;
    }

    *::-moz-scrollbar-track {
        background: #FFF;
    }

    *::-moz-scrollbar-thumb {
        background: #C4CDD6;
        border-radius: 8px;
    }

    *::-moz-scrollbar-thumb:hover {
        background: #555;
    }

    *::-webkit-scrollbar-corner {
        background: rgba(0,0,0,0);
    }
`;

export const SmartShapes = ({
    locale,
    tenantId,
    hostApp,
    authConfig,
    layout = PreviewLayout.PreviewOnTop,
    dragAndDrop,
}: SmartShapesProps): JSX.Element => {
    i18n.init({
        lng: locale,
        fallbackLng: SupportedLanguages.find((l) => l.default)?.code || 'en-US',
        resources,
    });
    const theme = useTheme();
    const { isSmallDevice } = useBreakpointInfo();
    const [selectedShape, setSelectedShape] = useState<Shape>(getShapes()[0]);
    const [isEditingShape, setIsEditingShape] = useState(false);
    const [isFetchingShapes, setIsFetchingShapes] = useState(false);
    const [shapeList, setShapeList] = useState<{ collectionName: string; items: Shape[] }[]>([
        {
            collectionName: 'Basic Shape',
            items: [...getDefaultShapes(), ...getShapes()],
        },
    ]);

    useEffect(() => {
        if (dragAndDrop) {
            enbaleDragAndDrop(dragAndDrop);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setAppUser({ tenantId, hostApp, authConfig });
    }, [authConfig, hostApp, tenantId]);

    const handleShapeSelect = (shape: Shape): void => {
        setSelectedShape(shape);
        setIsEditingShape(true);
    };

    const onEditCancel = () => {
        setIsEditingShape(false);
    };

    const handleShapeDrag = (shape: Shape): void => {
        setSelectedShape(shape);
    };

    const setOptionValue = (optionId: string, value: OptionValueType): void => {
        if (selectedShape) {
            const options = selectedShape.options
                ? selectedShape.options.map((option) => (optionId === option.id ? { ...option, value } : option))
                : [];

            setSelectedShape({ ...selectedShape, options });
        }
    };

    const getAssets = async () => {
        setIsFetchingShapes(true);
        const collections = await getShapesAssetList();
        if (collections.length) {
            setShapeList([...shapeList, ...collections]);
        }
        setIsFetchingShapes(false);
    };

    useEffect(() => {
        setCurrentShape(selectedShape);
    }, [selectedShape]);

    useEffect(() => {
        if (authConfig.value) getAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authConfig.value]);

    const flipHandler = (flip: FlipOptions) => {
        setSelectedShape({ ...selectedShape, horizontalFlip: flip.horizontalFlip, verticalFlip: flip.verticalFlip });
    };

    return (
        <div
            className={`smart-shapes ${css`
                ${getLayoutContainerStyle()}
                ${!isSmallDevice && customScrollStyle}
            `}`}
        >
            {/* eslint-disable-next-line react/jsx-fragments */}
            <Fragment>
                {!isEditingShape ? (
                    <div className='smart-shapes__list-view'>
                        <ShapeTypeList
                            shapeList={shapeList}
                            isFetchingShapes={isFetchingShapes}
                            onSelect={handleShapeSelect}
                            isDraggable={!!dragAndDrop}
                            onDrag={dragAndDrop ? handleShapeDrag : undefined}
                        />
                    </div>
                ) : (
                    <div className='smart-shapes__editor-view'>
                        <EditorView
                            shape={selectedShape}
                            isDraggable={!!dragAndDrop}
                            theme={theme}
                            layout={layout}
                            onEditCancel={onEditCancel}
                            setOptionValue={setOptionValue}
                            flip={flipHandler}
                        />
                    </div>
                )}
            </Fragment>
        </div>
    );
};
